
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

type ReferralRule = Tables<"referral_rules">;
type ReferralRuleInsert = TablesInsert<"referral_rules">;
type ReferralRuleUpdate = TablesUpdate<"referral_rules">;

interface ReferralRuleForm {
  referred_discount_type: "percentage" | "fixed_value";
  referred_discount_value: string;
  referred_min_purchase: string;
  referrer_bonus_type: "percentage" | "fixed_value";
  referrer_bonus_value: string;
  referrer_multiplier: string;
  allow_bonus_accumulation: boolean;
}

export const useReferralRules = () => {
  const [activeRule, setActiveRule] = useState<ReferralRule | null>(null);
  const [formData, setFormData] = useState<ReferralRuleForm>({
    referred_discount_type: "percentage",
    referred_discount_value: "10",
    referred_min_purchase: "50",
    referrer_bonus_type: "fixed_value", // Sempre valor fixo
    referrer_bonus_value: "20",
    referrer_multiplier: "100", // Agora representa valor mínimo para usar bônus
    allow_bonus_accumulation: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { toast } = useToast();

  // Carregar regra ativa
  const loadActiveRule = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("referral_rules")
        .select("*")
        .eq("is_active", true)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setActiveRule(data);
        setFormData({
          referred_discount_type: data.referred_discount_type as "percentage" | "fixed_value",
          referred_discount_value: data.referred_discount_value.toString(),
          referred_min_purchase: data.referred_min_purchase.toString(),
          referrer_bonus_type: "fixed_value", // Sempre valor fixo
          referrer_bonus_value: data.referrer_bonus_value.toString(),
          referrer_multiplier: data.referrer_multiplier.toString(),
          allow_bonus_accumulation: data.allow_bonus_accumulation
        });
      }
    } catch (error) {
      console.error("Error loading active rule:", error);
      toast({
        title: "Erro ao carregar regra",
        description: "Não foi possível carregar a regra ativa",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Salvar nova regra ou atualizar existente
  const saveRule = async (): Promise<boolean> => {
    setIsSaving(true);
    try {
      // Obter o user_id atual
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("Usuário não autenticado");
      }

      const ruleData = {
        referred_discount_type: formData.referred_discount_type,
        referred_discount_value: parseFloat(formData.referred_discount_value),
        referred_min_purchase: parseFloat(formData.referred_min_purchase),
        referrer_bonus_type: "fixed_value", // Sempre valor fixo
        referrer_bonus_value: parseFloat(formData.referrer_bonus_value),
        referrer_multiplier: parseInt(formData.referrer_multiplier),
        allow_bonus_accumulation: formData.allow_bonus_accumulation
      };

      let result;
      if (activeRule) {
        // Atualizar regra existente
        result = await supabase
          .from("referral_rules")
          .update(ruleData)
          .eq("id", activeRule.id)
          .select()
          .single();
      } else {
        // Criar nova regra
        result = await supabase
          .from("referral_rules")
          .insert({
            ...ruleData,
            user_id: user.id,
            is_active: true
          })
          .select()
          .single();
      }

      if (result.error) throw result.error;

      // Se não havia regra ativa antes, ativar a nova
      if (!activeRule && result.data) {
        await supabase.rpc("activate_referral_rule", { rule_id: result.data.id });
      }

      setActiveRule(result.data);
      setHasUnsavedChanges(false);

      toast({
        title: "Regra salva",
        description: "As regras de indicação foram salvas com sucesso",
      });

      return true;
    } catch (error) {
      console.error("Error saving rule:", error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar as regras de indicação",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Resetar formulário para valores salvos
  const resetForm = () => {
    if (activeRule) {
      setFormData({
        referred_discount_type: activeRule.referred_discount_type as "percentage" | "fixed_value",
        referred_discount_value: activeRule.referred_discount_value.toString(),
        referred_min_purchase: activeRule.referred_min_purchase.toString(),
        referrer_bonus_type: "fixed_value", // Sempre valor fixo
        referrer_bonus_value: activeRule.referrer_bonus_value.toString(),
        referrer_multiplier: activeRule.referrer_multiplier.toString(),
        allow_bonus_accumulation: activeRule.allow_bonus_accumulation
      });
    } else {
      setFormData({
        referred_discount_type: "percentage",
        referred_discount_value: "10",
        referred_min_purchase: "50",
        referrer_bonus_type: "fixed_value", // Sempre valor fixo
        referrer_bonus_value: "20",
        referrer_multiplier: "100", // Valor mínimo para usar bônus
        allow_bonus_accumulation: true
      });
    }
    setHasUnsavedChanges(false);
  };

  // Atualizar campo do formulário
  const updateFormField = (field: keyof ReferralRuleForm, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    loadActiveRule();
  }, []);

  return {
    activeRule,
    formData,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    saveRule,
    resetForm,
    updateFormField,
    reloadActiveRule: loadActiveRule
  };
};
